import { FC, useRef, useCallback, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@crisellevaldez/speculo-ui';
import { Lightbulb, X } from 'lucide-react';
import { StatCard, StatCardProps } from './StatCard';
import { getLockedWidgets, type WidgetSize } from '../../stores/useWidgetPreferences';
import { useFlywheelEnabled } from '../../stores/useOrganizationStore';

interface SortableStatCardProps {
  card: StatCardProps;
  onRemove?: () => void;
  colSpan?: WidgetSize;
  onResize?: (size: WidgetSize) => void;
  highlighted?: boolean;
  onToggleHighlight?: () => void;
}

export const SortableStatCard: FC<SortableStatCardProps> = ({
  card,
  onRemove,
  colSpan = 1,
  onResize,
  highlighted,
  onToggleHighlight,
}) => {
  const flywheelEnabled = useFlywheelEnabled();
  const lockedWidgets = getLockedWidgets(flywheelEnabled);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: card.id,
  });

  const [resizePreview, setResizePreview] = useState<WidgetSize | null>(null);
  const targetSizeRef = useRef<WidgetSize>(colSpan);
  const resizeState = useRef<{ startX: number; colWidth: number } | null>(null);
  const rafRef = useRef<number>(0);

  const handleResizeStart = useCallback(
    (e: React.PointerEvent) => {
      if (!onResize) return;
      e.preventDefault();
      e.stopPropagation();

      const el = e.currentTarget.closest('[data-widget-cell]') as HTMLElement | null;
      if (!el) return;

      const colWidth = el.offsetWidth;
      resizeState.current = { startX: e.clientX, colWidth };
      targetSizeRef.current = colSpan;
      setResizePreview(colSpan);

      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);

      const onMove = (ev: PointerEvent) => {
        if (!resizeState.current) return;
        cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(() => {
          if (!resizeState.current) return;
          const dx = ev.clientX - resizeState.current.startX;
          const threshold = resizeState.current.colWidth * 0.35;

          let next: WidgetSize;
          if (colSpan === 1) {
            next = dx > threshold ? 2 : 1;
          } else {
            next = dx < -threshold ? 1 : 2;
          }
          if (next !== targetSizeRef.current) {
            targetSizeRef.current = next;
            setResizePreview(next);
          }
        });
      };

      const onUp = () => {
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        cancelAnimationFrame(rafRef.current);
        const finalSize = targetSizeRef.current;
        if (finalSize !== colSpan) {
          onResize(finalSize);
        }
        resizeState.current = null;
        setResizePreview(null);
      };

      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
    },
    [onResize, colSpan]
  );

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  const effectiveSpan = resizePreview ?? colSpan;

  const cardWithHighlight: StatCardProps =
    highlighted !== undefined ? { ...card, highlighted } : card;

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-widget-cell=""
      className={cn('relative group grid', effectiveSpan === 2 && 'sm:col-span-2')}
    >
      {/* Drag zone: only the card area has drag listeners */}
      <div
        {...attributes}
        {...listeners}
        className="w-full grid cursor-grab active:cursor-grabbing [&>*]:h-full [&>*>div:first-child]:h-full"
      >
        <div className="h-full">
          <StatCard {...cardWithHighlight} />
        </div>
      </div>

      {/* Controls: OUTSIDE the drag listener div so clicks work freely */}
      <div className="absolute bottom-2 right-2 z-30 flex items-center gap-0.5 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity">
        {onToggleHighlight && (
          <button
            onClick={() => onToggleHighlight()}
            className={cn(
              'p-1 rounded transition-colors cursor-pointer',
              highlighted
                ? 'text-amber-500 dark:text-amber-300 hover:text-amber-600 dark:hover:text-amber-200 hover:bg-amber-50 dark:hover:bg-amber-900/30'
                : 'text-foreground/40 hover:text-amber-500 dark:hover:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/30'
            )}
            aria-label="Toggle highlight"
          >
            <Lightbulb className="w-3.5 h-3.5" />
          </button>
        )}
        {onRemove && !lockedWidgets.includes(card.id) && (
          <button
            onClick={() => onRemove()}
            className="p-1 rounded text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors cursor-pointer"
            aria-label="Remove widget"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Right-edge resize handle */}
      {onResize && (
        <div
          onPointerDown={handleResizeStart}
          style={{ touchAction: 'none' }}
          className="absolute top-0 right-0 w-3 h-full z-30 cursor-col-resize group/resize invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <div className="absolute top-1/2 right-0.5 -translate-y-1/2 w-1 h-8 rounded-full bg-foreground/20 group-hover/resize:bg-amber-500 transition-colors" />
        </div>
      )}

      {/* Resize preview indicator */}
      {resizePreview !== null && resizePreview !== colSpan && (
        <div className="absolute inset-0 rounded-xl border-2 border-dashed border-amber-500/60 pointer-events-none z-10" />
      )}
    </div>
  );
};
